import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useSearchParams } from 'react-router-dom';

// Modal Lightbox Component
const ImageModal = ({ isOpen, images, startIndex, onClose, categoryTitle }) => {
  const [currentIndex, setCurrentIndex] = useState(startIndex);

  if (!isOpen) return null;

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const goToImage = (index) => {
    setCurrentIndex(index);
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.95)',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '20px'
      }}
      onClick={onClose}
    >
      {/* Image Container */}
      <div 
        className="gallery-modal-image-container"
        style={{ 
          width: '90vw',
          maxWidth: '1200px',
          height: '70vh',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          borderRadius: '8px'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button - positioned above image */}
        <button
          onClick={onClose}
          className="gallery-modal-close-btn"
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            background: 'rgba(255, 255, 255, 0.2)',
            border: 'none',
            color: 'white',
            fontSize: '30px',
            cursor: 'pointer',
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background 0.3s',
            zIndex: 100
          }}
          onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
          onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
        >
          ×
        </button>
        <img
          src={images[currentIndex].src}
          alt={images[currentIndex].title}
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            width: 'auto',
            height: 'auto',
            objectFit: 'contain',
            borderRadius: '8px',
            transition: 'opacity 0.3s ease-in-out'
          }}
        />

        {/* Navigation Arrows */}
        {images.length > 1 && (
          <>
            <button
              onClick={prevImage}
              style={{
                position: 'absolute',
                left: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'rgba(110, 34, 144, 0.8)',
                border: 'none',
                color: 'white',
                fontSize: '24px',
                cursor: 'pointer',
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.3s',
                zIndex: 10
              }}
              onMouseEnter={(e) => e.target.style.background = 'rgba(110, 34, 144, 1)'}
              onMouseLeave={(e) => e.target.style.background = 'rgba(110, 34, 144, 0.8)'}
            >
              <i className="fa fa-chevron-left"></i>
            </button>

            <button
              onClick={nextImage}
              style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'rgba(110, 34, 144, 0.8)',
                border: 'none',
                color: 'white',
                fontSize: '24px',
                cursor: 'pointer',
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.3s',
                zIndex: 10
              }}
              onMouseEnter={(e) => e.target.style.background = 'rgba(110, 34, 144, 1)'}
              onMouseLeave={(e) => e.target.style.background = 'rgba(110, 34, 144, 0.8)'}
            >
              <i className="fa fa-chevron-right"></i>
            </button>
          </>
        )}
      </div>

      {/* Image Info */}
      <div style={{ 
        marginTop: '20px', 
        textAlign: 'center',
        color: 'white'
      }}>
        <h3 style={{ margin: '10px 0', fontSize: '20px' }}>{categoryTitle}</h3>
        <p style={{ margin: '5px 0', fontSize: '14px', opacity: 0.8 }}>
          {images[currentIndex].title}
        </p>
        <p style={{ margin: '5px 0', fontSize: '14px' }}>
          {currentIndex + 1} / {images.length}
        </p>
      </div>

      {/* Thumbnail Navigation */}
      {images.length > 1 && (
        <div style={{
          display: 'flex',
          gap: '10px',
          marginTop: '20px',
          flexWrap: 'wrap',
          justifyContent: 'center',
          maxWidth: '90%',
          overflowX: 'auto'
        }}>
          {images.map((image, index) => (
            <img
              key={index}
              src={image.src}
              alt={`Thumbnail ${index + 1}`}
              onClick={() => goToImage(index)}
              style={{
                width: '60px',
                height: '60px',
                objectFit: 'cover',
                borderRadius: '5px',
                cursor: 'pointer',
                border: currentIndex === index ? '3px solid #6e2290' : '3px solid transparent',
                opacity: currentIndex === index ? 1 : 0.6,
                transition: 'all 0.3s'
              }}
              onMouseEnter={(e) => e.target.style.opacity = '1'}
              onMouseLeave={(e) => e.target.style.opacity = currentIndex === index ? '1' : '0.6'}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const Gallery = () => {
  const [searchParams] = useSearchParams();
  const [modalState, setModalState] = useState({
    isOpen: false,
    images: [],
    startIndex: 0,
    categoryTitle: ''
  });

  const openModal = (category, imageIndex = 0) => {
    setModalState({
      isOpen: true,
      images: category.images,
      startIndex: imageIndex,
      categoryTitle: category.title
    });
  };

  const closeModal = () => {
    setModalState({
      isOpen: false,
      images: [],
      startIndex: 0,
      categoryTitle: ''
    });
  };

  // Additional images from image_new for categories 2 and 3
  const imageNewForGallery = [
    '/images/image_new/photo_1_2025-12-19_08-37-19.jpg',
    '/images/image_new/photo_1_2025-12-19_08-38-16.jpg',
    '/images/image_new/photo_10_2025-12-19_08-37-19.jpg',
    '/images/image_new/photo_11_2025-12-19_08-37-19.jpg',
    '/images/image_new/photo_12_2025-12-19_08-37-19.jpg',
    '/images/image_new/photo_13_2025-12-19_08-37-19.jpg',
    '/images/image_new/photo_14_2025-12-19_08-37-19.jpg',
    '/images/image_new/photo_15_2025-12-19_08-37-19.jpg',
    '/images/image_new/photo_16_2025-12-19_08-37-19.jpg',
    '/images/image_new/photo_17_2025-12-19_08-37-19.jpg',
    '/images/image_new/photo_18_2025-12-19_08-37-19.jpg',
    '/images/image_new/photo_19_2025-12-19_08-37-19.jpg',
    '/images/image_new/photo_2_2025-12-19_08-37-19.jpg',
    '/images/image_new/photo_2_2025-12-19_08-38-16.jpg',
    '/images/image_new/photo_20_2025-12-19_08-37-19.jpg',
    '/images/image_new/photo_21_2025-12-19_08-37-19.jpg',
    '/images/image_new/photo_22_2025-12-19_08-37-19.jpg',
    '/images/image_new/photo_23_2025-12-19_08-37-19.jpg',
    '/images/image_new/photo_24_2025-12-19_08-37-19.jpg',
    '/images/image_new/photo_25_2025-12-19_08-37-19.jpg',
    '/images/image_new/photo_26_2025-12-19_08-37-19.jpg',
    '/images/image_new/photo_3_2025-12-19_08-37-19.jpg',
    '/images/image_new/photo_3_2025-12-19_08-38-16.jpg',
    '/images/image_new/photo_4_2025-12-19_08-37-19.jpg',
    '/images/image_new/photo_4_2025-12-19_08-38-16.jpg',
    '/images/image_new/photo_5_2025-12-19_08-37-19.jpg',
    '/images/image_new/photo_5_2025-12-19_08-38-16.jpg',
    '/images/image_new/photo_6_2025-12-19_08-37-19.jpg',
    '/images/image_new/photo_7_2025-12-19_08-37-19.jpg',
    '/images/image_new/photo_8_2025-12-19_08-37-19.jpg',
    '/images/image_new/photo_9_2025-12-19_08-37-19.jpg',
  ];

  // Get 5 random images from image_new - memoized to keep them fixed
  const randomImagesForCategory2 = useMemo(() => {
    const shuffled = [...imageNewForGallery].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 5).map((src, idx) => ({
      src,
      title: `Gallery Image ${idx + 1}`
    }));
  }, []);

  const randomImagesForCategory3 = useMemo(() => {
    const shuffled = [...imageNewForGallery].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 5).map((src, idx) => ({
      src,
      title: `Gallery Image ${idx + 1}`
    }));
  }, []);

  const galleryCategories = [
    {
      id: 1,
      title: 'Formation Manucure',
      description: "Profitez d'une remise exceptionnelle sur votre manucure classique.",
      mainImage: '/images/galery/formation1.png',
      images: [
        { src: '/images/galery/formation1.png', title: 'Formation Manucure - Basic Course' },
        { src: '/images/galery/formation2.png', title: 'Formation Manucure - Advanced Techniques' },
        { src: '/images/galery/formation3.png', title: 'Formation Manucure - Workshop' },
        { src: '/images/galery/formation4.png', title: 'Formation Manucure - Workshop' },
        { src: '/images/galery/formation5.png', title: 'Formation Manucure - Workshop' },
        { src: '/images/galery/formation6.png', title: 'Formation Manucure - Workshop' },
        { src: '/images/galery/formation7.png', title: 'Formation Manucure - Workshop' },
        { src: '/images/art/photo_1_2025-12-19_09-25-02.jpg', title: 'Art - Photo 1' },
        { src: '/images/art/photo_2_2025-12-19_09-25-02.jpg', title: 'Art - Photo 2' },
        { src: '/images/art/photo_3_2025-12-19_09-25-02.jpg', title: 'Art - Photo 3' },
      ],
    },
    {
      id: 2,
      title: 'Nail Art',
      description: 'Formation de Base Manucure Russe.',
      mainImage: '/images/galery/2501C3FE-5AB2-4757-9054-AF4DB23E5AA8.jpg',
      images: [
        { src: '/images/galery/2501C3FE-5AB2-4757-9054-AF4DB23E5AA8.jpg', title: 'Nail Art - Creative Design' },
        { src: '/images/galery/1024E8D8-4181-45E0-A8EA-59B538853213.jpg', title: 'Nail Art - Russian Style' },
        { src: '/images/galery/1f6f909e-bd4e-4ca8-b9a7-71bea9e87d16.jpg', title: 'Nail Art - Glitter Design' },
        { src: '/images/galery/075F32sd20717-4507-877D-2B60DAC46B42.jpg', title: 'Nail Art - Glitter Design' },
        { src: '/images/manic_3.png', title: 'Nail Art - Glitter Design' },
        ...randomImagesForCategory2
      ],
    },
    {
      id: 3,
      title: 'Manicure Classique',
      description: 'Manicure et pose vernis Gel',
      mainImage: '/images/manic-23.png',
      images: [
        { src: '/images/manic-23.png', title: 'Classic Manicure' },
        { src: '/images/manic-5.jpg', title: 'Gel Polish Application' },
        { src: '/images/manic_7.jpg', title: 'French Manicure' },
        ...randomImagesForCategory3
      ],
    },
    {
      id: 4,
      title: 'Pédicure',
      description: 'Pédicure hygiénique complète avec vernis semi permanent(gel)',
      mainImage: '/images/ped2.png',
      images: [
        { src: '/images/ped2.png', title: 'Pedicure - Hygienic Treatment' },
        { src: '/images/ped_1.jpeg', title: 'Pedicure with Gel Polish' },
        { src: '/images/ped_3.jpg', title: 'Spa Pedicure' },
      ],
    },
  ];

  // Open category from URL parameter
  useEffect(() => {
    const categoryId = searchParams.get('category');
    if (categoryId) {
      const category = galleryCategories.find(cat => cat.id === parseInt(categoryId));
      if (category) {
        openModal(category, 0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <>
      <Helmet>
        <title>Galerie | Royal Nails Pro - Photos Manucure, Pédicure, Nail Art Genève</title>
        <link rel="canonical" href="https://royalnailspro.ch/gallery" />
        <meta
          name="description"
          content="Découvrez notre galerie de réalisations en manucure, pédicure, nail art et formations. Explorez nos créations et techniques professionnelles à Genève."
        />
        <meta
          name="keywords"
          content="galerie manucure Genève, photos nail art, réalisations pédicure, portfolio ongles Genève, exemples manucure, nail art designs, formations manucure photos"
        />
        
        {/* Open Graph / Facebook & Instagram */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://royalnailspro.ch/gallery" />
        <meta property="og:title" content="Galerie | Royal Nails Pro - Photos Manucure, Pédicure, Nail Art Genève" />
        <meta property="og:description" content="Découvrez notre galerie de réalisations en manucure, pédicure, nail art et formations à Genève." />
        <meta property="og:image" content="https://royalnailspro.ch/images/galery/formation1.png" />
        <meta property="og:image:secure_url" content="https://royalnailspro.ch/images/galery/formation1.png" />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:image:alt" content="Galerie Royal Nails Pro - Réalisations manucure" />
        <meta property="article:author" content="https://www.facebook.com/people/Rodyka-Soltan/61572131475024/" />
        
        {/* Instagram specific */}
        <meta property="instagram:creator" content="@rodyka_soltan" />
        
        {/* TikTok */}
        <meta name="tiktok:creator" content="@rodicasoltan" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Galerie | Royal Nails Pro" />
        <meta name="twitter:description" content="Découvrez notre galerie de réalisations en manucure, pédicure et nail art." />
        <meta name="twitter:image" content="https://royalnailspro.ch/images/galery/formation1.png" />
        <meta name="twitter:site" content="@rodyka_soltan" />
        <meta name="twitter:creator" content="@rodyka_soltan" />
      </Helmet>

      <div className="back_re">
        <div className="container">
          <div className="row">
            <div className="col-md-12">
              <div className="title">
                <h2>Galerie photos</h2>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="gallery-section py-5">
        <div className="container">
          <div className="row">
            <div className="col-md-12 text-center mb-4">
              <div className="titlepage">
                <p>Explorez nos créations - Cliquez pour agrandir</p>
              </div>
            </div>
          </div>

          <div className="row g-4">
            {galleryCategories.map((category) => (
              <div key={category.id} className="col-md-3 col-sm-6">
                <div 
                  className="gallery-box" 
                  onClick={() => openModal(category, 0)}
                  style={{ 
                    cursor: 'pointer',
                    transition: 'transform 0.3s, box-shadow 0.3s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(110, 34, 144, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
                  }}
                >
                  <img src={category.mainImage} alt={category.title} className="img-fluid" style={{ borderRadius: '8px' }} />
                  <div className="gallery-info" style={{ marginTop: '15px', textAlign: 'center' }}>
                    <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#6e2290', marginBottom: '8px' }}>
                      {category.title}
                    </h3>
                    <p style={{ fontSize: '13px', color: '#666', marginBottom: '5px' }}>
                      {category.description}
                    </p>
                    <p style={{ fontSize: '12px', color: '#999' }}>
                      {category.images.length} photos
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Modal */}
      <ImageModal
        isOpen={modalState.isOpen}
        images={modalState.images}
        startIndex={modalState.startIndex}
        onClose={closeModal}
        categoryTitle={modalState.categoryTitle}
      />
    </>
  );
};

export default Gallery;
